import React, { useState, useEffect, useRef } from 'react';
import {
  GoogleMap,
  StandaloneSearchBox,
  Marker,
  Circle,
  InfoWindow,
} from '@react-google-maps/api';
import { BiTargetLock } from 'react-icons/bi';
import { changeRadius } from '../store/slices/carsTrucks.slice';
import { useDispatch } from 'react-redux';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import useToggle from '../hooks/useToggle';
import Cookies from 'js-cookie';

const Gmap = ({ onChangeLoc, data }) => {
  const defaultCoordinates = { lat: 33.7488, lng: -84.3877 };
  const [center, setCenter] = useState(defaultCoordinates);
  const [searchBox, setSearchBox] = useState(null);
  const [radius, setRadius] = useState(100); // Radius in miles
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(7); // Initial zoom level adjusted for a wider view
  const [toggleMap, handleToggleMap] = useToggle();

  // Cookie
  const cookieExpiryDays = 7; // Cookie will expire in 7 days

  const dispatch = useDispatch();

  const mapContainerStyle = {
    width: '100%',
    height: '300px',
  };

  // Ref to store the map instance
  const mapRef = useRef(null);
  // Ref to store the search box instance
  const searchBoxRef = useRef(null);

  useEffect(() => {
    const locationCookie = Cookies.get('location');

    // Check if location cookie is available
    if (locationCookie) {
      const location = JSON.parse(locationCookie);
      setCenter({
        lat: location.lat,
        lng: location.lng,
      });
    } else {
      if (navigator.permissions) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then((permissionStatus) => {
            if (
              permissionStatus.state === 'granted' ||
              permissionStatus.state === 'prompt'
            ) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  setCenter({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  });
                },
                () => {
                  // User denied geolocation or error occurred, using default coordinates
                  setCenter(defaultCoordinates);
                }
              );
            } else if (permissionStatus.state === 'denied') {
              // Permission denied, using default coordinates
              setCenter(defaultCoordinates);
            }
          })
          .catch(() => {
            // Handle error if geolocation permissions API not supported
            setCenter(defaultCoordinates);
          });
      } else {
        // Handle error if geolocation API not supported
        setCenter(defaultCoordinates);
      }
    }
  }, []);

  useEffect(() => {
    // Adjust zoom level based on the radius
    const calculateZoomLevel = (radiusInMiles) => {
      const radiusInMeters = radiusInMiles * 1609.34;
      if (radiusInMeters > 10000) return 7;
      if (radiusInMeters > 5000) return 8;
      if (radiusInMeters > 1000) return 9;
      if (radiusInMeters > 500) return 10;
      if (radiusInMeters > 100) return 11;
      return 12;
    };
    setZoom(calculateZoomLevel(radius));
  }, [radius]);

  const onLoad = (map) => {
    mapRef.current = map;
  };

  const onPlacesChanged = () => {
    if (searchBox !== null) {
      const places = searchBox.getPlaces();
      if (places.length === 0) return;

      const place = places[0];
      if (!place.geometry) return;

      setCenter({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      onChangeLoc(lat, lng);
    }
  };

  const handleSearchBoxLoad = (ref) => {
    setSearchBox(ref);
    searchBoxRef.current = ref;
  };

  const handleRadiusChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setRadius(value);
    dispatch(changeRadius(value));
  };

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  const handleTargetClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const currentCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCenter(currentCoords);
        if (searchBoxRef.current) {
          const autocompleteService =
            new window.google.maps.places.AutocompleteService();
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: currentCoords }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const address = results[0].formatted_address;
              const input = document.querySelector('.target-input');
              if (input) {
                input.value = address;
                const lat = currentCoords.lat;
                const lng = currentCoords.lng;
                onChangeLoc(lat, lng);
              }
              const bounds = new window.google.maps.LatLngBounds();
              bounds.extend(currentCoords);
              searchBoxRef.current.setBounds(bounds);
            }
          });
        }
      });
    }
  };

  const getRandomOffset = () => {
    // Generate a random number between -0.0002 and 0.0002 (adjust as needed)
    const offset = (Math.random() - 0.5) * 0.0004;
    return offset;
  };

  return (
    <>
      <div className='hide-mobile'>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center} // Use current center state
          zoom={zoom} // Use current zoom state
          onLoad={onLoad}
        >
          {/* Render markers for each car */}
          {data.map((car) => {
            let position = {};
            let title = '';
            if (car.dealer) {
              position = {
                lat: parseFloat(car.dealer.latitude) + getRandomOffset(),
                lng: parseFloat(car.dealer.longitude) + getRandomOffset(),
              };
              title = car.heading;
            } else if (car.meta) {
              position = {
                lat: parseFloat(car.meta.hp_latitude) + getRandomOffset(),
                lng: parseFloat(car.meta.hp_longitude) + getRandomOffset(),
              };
              title = car.title.rendered || car.heading; // Ensure title is a string or handle accordingly
            }

            return (
              <Marker
                key={car.id}
                position={position}
                onClick={() => handleMarkerClick(car)}
                title={title} // Ensure 'title' is a string
              >
                {/* InfoWindow to show car name and link */}
                {selectedMarker === car && (
                  <InfoWindow
                    position={position}
                    onCloseClick={handleInfoWindowClose}
                  >
                    <div>
                      <h3>{title}</h3>
                      {car.meta && <p>{car.meta.hp_location}</p>}
                      <a
                        href={`${car?.link || car?.vdp_url}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='car-info'
                      >
                        More Info
                      </a>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}

          {/* Circle to show the selected radius */}
          <Circle
            center={center}
            radius={radius * 1609.34} // Convert miles to meters
            options={{
              fillColor: '#4285F4',
              fillOpacity: 0.35,
              strokeColor: '#4285F4',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              clickable: false,
              draggable: false,
              editable: false,
              visible: true,
              zIndex: 1,
            }}
          />
        </GoogleMap>
      </div>

      <div className='controls-container'>
        <div className='autocomplete-container'>
          <StandaloneSearchBox
            onLoad={handleSearchBoxLoad}
            onPlacesChanged={onPlacesChanged}
          >
            <input
              type='text'
              placeholder='Enter desired location, address, or zip code'
              className='target-input form-control'
            />
          </StandaloneSearchBox>
          <BiTargetLock
            className='target-button'
            onClick={handleTargetClick}
            size={20}
          />
        </div>
        <div className='radius-input'>
          <div className='radius-wrap'>
            <span>Show results within</span>
            <input
              type='number'
              value={radius}
              className='form-control'
              min='20'
              max='100'
              step={10}
              onChange={handleRadiusChange}
              onKeyDown={(e) => e.preventDefault()}
            />
            <span>miles</span>
          </div>
        </div>
      </div>

      <div className='hide-desktop' style={{ marginTop: '-20px' }}>
        <button className='btn-view-map' onClick={handleToggleMap}>
          View Map{' '}
          {toggleMap ? <FaMinusCircle size={18} /> : <FaPlusCircle size={18} />}
        </button>
        {toggleMap && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center} // Use current center state
            zoom={zoom} // Use current zoom state
            onLoad={onLoad}
          >
            {/* Render markers for each car */}
            {data.map((car) => {
              let position = {};
              let title = '';
              if (car.dealer) {
                position = {
                  lat: parseFloat(car.dealer.latitude) + getRandomOffset(),
                  lng: parseFloat(car.dealer.longitude) + getRandomOffset(),
                };
                title = car.heading;
              } else if (car.meta) {
                position = {
                  lat: parseFloat(car.meta.hp_latitude) + getRandomOffset(),
                  lng: parseFloat(car.meta.hp_longitude) + getRandomOffset(),
                };
                title = car.title.rendered || car.heading; // Ensure title is a string or handle accordingly
              }

              return (
                <Marker
                  key={car.id}
                  position={position}
                  onClick={() => handleMarkerClick(car)}
                  title={title} // Ensure 'title' is a string
                >
                  {/* InfoWindow to show car name and link */}
                  {selectedMarker === car && (
                    <InfoWindow
                      position={position}
                      onCloseClick={handleInfoWindowClose}
                    >
                      <div>
                        <h3>{title}</h3>
                        {car.meta && <p>{car.meta.hp_location}</p>}
                        <a
                          href={`${car?.link || car?.vdp_url}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='car-info'
                        >
                          More Info
                        </a>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              );
            })}

            {/* Circle to show the selected radius */}
            <Circle
              center={center}
              radius={radius * 1609.34} // Convert miles to meters
              options={{
                fillColor: '#4285F4',
                fillOpacity: 0.35,
                strokeColor: '#4285F4',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: false,
                draggable: false,
                editable: false,
                visible: true,
                zIndex: 1,
              }}
            />
          </GoogleMap>
        )}
      </div>
    </>
  );
};

export default Gmap;
